import React, { useMemo } from "react";
import { noop } from "@common/helpers/function";
import { FiltersTypes } from "@common/types/filters/general";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { ModeRadioGroup } from "./ModeRadioGroup";
import styles from "./ReviewFilter.module.scss";

export type ReviewerMode = NonNullable<FiltersTypes["reviewerMode"]>;

const REVIEWER_MODE_OPTIONS: Array<{ value: ReviewerMode; label: string }> = [
    { value: "any", label: "Any member" },
    { value: "all", label: "All members" },
    { value: "none", label: "None of the members" },
];

interface ReviewerFilterSectionProps {
    modeName: string;
    idsName: string;
    reviewerIds: number[];
    reviewerMode: ReviewerMode;
    onModeChange: (mode: ReviewerMode) => void;
    onReviewerIdsChange: (reviewerIds: number[] | undefined) => void;
}

export const ReviewerFilterSection: React.FC<ReviewerFilterSectionProps> = ({
    modeName,
    idsName,
    reviewerIds,
    reviewerMode,
    onModeChange,
    onReviewerIdsChange,
}) => {
    const { data: members = [] } = useCurrentWorkspaceMembers();

    const memberOptions = useMemo(
        (): Array<FilterMultiselectOption<number>> =>
            members.map((member) => ({
                value: member.user.id,
                label: member.user.name,
                searchableLabel: member.user.name,
            })),
        [members],
    );

    return (
        <>
            <div className={styles.sectionWithTopBorder}>
                <div className={styles.sectionTitle}>Reviewers</div>

                <ModeRadioGroup
                    name={modeName}
                    value={reviewerMode}
                    options={REVIEWER_MODE_OPTIONS}
                    onChange={onModeChange}
                />
            </div>

            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    options={memberOptions}
                    value={reviewerIds}
                    searchable={false}
                    onChange={(ids) =>
                        onReviewerIdsChange(ids.length > 0 ? ids : undefined)
                    }
                    name={idsName}
                    onBlur={noop}
                />
            </div>
        </>
    );
};

interface UseReverseReviewersParams {
    reviewerIds: number[];
    onReviewerIdsChange: (reviewerIds: number[]) => void;
}

export const useReverseReviewers = ({
    reviewerIds,
    onReviewerIdsChange,
}: UseReverseReviewersParams): (() => void) | undefined => {
    const { data: members = [] } = useCurrentWorkspaceMembers();

    return useMemo(() => {
        if (reviewerIds.length === 0 || members.length === 0) {
            return undefined;
        }
        return () =>
            onReviewerIdsChange(
                members
                    .filter((member) => !reviewerIds.includes(member.user.id))
                    .map((member) => member.user.id),
            );
    }, [reviewerIds, members, onReviewerIdsChange]);
};
